import React, {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import {
  Row,
  Col,
  Popover,
  Input,
  Spin,
  Collapse,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import Colors from '../../constants/Colors';
import Debouncer from '../../helpers/Debouncer';
import {
  NONE_COSTCODE_OPTION,
  buildCostcodeFilterCategories,
  getCostcodeBucketFetchParams,
  makeCostcodeSearchParams,
} from '../reportHelpers';

/* eslint-disable react/prop-types -- layout subcomponents; public API validated below */
const { Panel } = Collapse;

const SEARCH_DEBOUNCE_MS = 300;
const BUCKET_NONE = { type: 'none', key: 'none' };
const BUCKET_GLOBAL = { type: 'global', key: 'global' };

const MENU_COLUMN_WIDTH = 228;
const MENU_LIST_HEIGHT = 228;
const OPTION_CHECKBOX_SPACE = 28;

const costcodeOptionShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
});

const getCostcodeLabel = (option, optionMap = {}) => (
  option?.label ?? optionMap[option?.value] ?? String(option?.value ?? '')
);

const getActiveGroupLabel = (bucket, projects = []) => {
  if (!bucket) return '';
  if (bucket.type === 'none') return 'No cost code';
  if (bucket.type === 'global') return 'Global';
  if (bucket.type === 'unphased') {
    const project = projects.find((p) => p.id === bucket.projectId);
    return project ? `${project.label} — Unphased` : 'Unphased';
  }
  if (bucket.type === 'phase') {
    return bucket.phaseName || 'Phase';
  }
  return '';
};

const buildInitialOptionMap = (initialOptions = []) => {
  const map = {};
  initialOptions.forEach((opt) => { map[opt.value] = opt.label; });
  return map;
};

const getSelectedOptions = (checked, optionMap) => {
  const byLabel = new Map();

  const addValue = (value) => {
    const label = getCostcodeLabel({ value, label: optionMap[value] }, optionMap);
    if (!byLabel.has(label)) {
      byLabel.set(label, { value, label, values: [value] });
      return;
    }
    const entry = byLabel.get(label);
    if (!entry.values.includes(value)) entry.values.push(value);
  };

  if (checked.has('None')) addValue('None');
  checked.forEach((value) => {
    if (value !== 'None') addValue(value);
  });

  return Array.from(byLabel.values()).sort((a, b) => {
    if (a.value === 'None') return -1;
    if (b.value === 'None') return 1;
    return String(a.label).localeCompare(String(b.label), undefined, { numeric: true });
  });
};

const dedupeOptionsByLabel = (options, optionMap) => {
  const byLabel = new Map();
  options.forEach((option) => {
    const label = getCostcodeLabel(option, optionMap);
    if (!byLabel.has(label)) {
      byLabel.set(label, { ...option, label, values: [option.value] });
      return;
    }
    const entry = byLabel.get(label);
    if (!entry.values.includes(option.value)) entry.values.push(option.value);
  });
  return Array.from(byLabel.values());
};

const getBrowseHint = (showNoneCostcodeOption, showGlobalCostcodes, t) => {
  const projectLabel = t('Project').toLowerCase();
  if (!showNoneCostcodeOption && !showGlobalCostcodes) {
    return `Expand a ${projectLabel}, then pick Unphased or a phase.`;
  }
  const items = [];
  if (showNoneCostcodeOption) items.push('None');
  if (showGlobalCostcodes) items.push('Global');
  items.push('Unphased', 'a phase');
  if (items.length === 2) return `Pick ${items[0]} or ${items[1]}.`;
  if (items.length === 3) return `Pick ${items[0]}, ${items[1]}, or ${items[2]}.`;
  return `Pick ${items.slice(0, -1).join(', ')}, or ${items[items.length - 1]}.`;
};

const filterBrowseGroups = (browseGroups, query) => {
  const normalized = String(query ?? '').trim().toLowerCase();
  if (!normalized) return browseGroups;

  return browseGroups.filter(
    (project) => project.label.toLowerCase().includes(normalized),
  );
};

const browseRowClassName = ({ isActive, indented, isNone }) => ([
  'report-costcode-filter-browse-row',
  isActive && 'report-costcode-filter-browse-row--active',
  indented && 'report-costcode-filter-browse-row--indented',
  isNone && 'report-costcode-filter-browse-row--none',
].filter(Boolean).join(' '));

function BrowseRow({
  label,
  isActive,
  indented = false,
  isNone = false,
  stopPropagation = false,
  onSelect,
}) {
  const handleActivate = (e) => {
    if (stopPropagation) e.stopPropagation();
    onSelect();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={browseRowClassName({ isActive, indented, isNone })}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleActivate(e);
      }}
    >
      {label}
    </div>
  );
}

function MenuColumn({
  title,
  children,
  isLast = false,
  browse = false,
}) {
  return (
    <div className={`report-costcode-filter-column${isLast ? ' report-costcode-filter-column--last' : ''}`}>
      <div className="report-costcode-filter-column-title">{title}</div>
      <div className={`report-costcode-filter-column-body${browse ? ' report-costcode-filter-column-body--browse' : ''}`}>
        {children}
      </div>
    </div>
  );
}

function OptionList({
  options,
  loading,
  listHeight = MENU_LIST_HEIGHT,
  listWidth = MENU_COLUMN_WIDTH - 12,
  checked,
  onToggleValue,
}) {
  const isOptionChecked = (option) => {
    const values = option?.values || [option.value];
    return values.some((value) => checked.has(value));
  };

  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ height: listHeight }}>
        <Spin size="small" />
      </Row>
    );
  }

  if (!options.length) {
    return (
      <div className="report-costcode-filter-empty" style={{ height: listHeight }}>
        No cost codes found.
      </div>
    );
  }

  const labelMaxWidth = listWidth - OPTION_CHECKBOX_SPACE;

  return (
    <div
      className="report-costcode-filter-option-list"
      style={{ height: listHeight, width: listWidth }}
    >
      {options.map((option) => {
        const key = option.values?.length > 1
          ? option.values.join('|')
          : option.value;
        return (
          <div key={key} className="report-costcode-filter-option">
            <OnTraccrCheckbox
              value={isOptionChecked(option)}
              onChange={(e) => onToggleValue(option, e)}
              label={getCostcodeLabel(option)}
              textStyle={{ maxWidth: labelMaxWidth, marginBottom: 0 }}
              checkboxStyle={{ marginBottom: 0 }}
            />
          </div>
        );
      })}
    </div>
  );
}

function BucketPanel({ label, isActive, onSelect }) {
  return (
    <Panel
      collapsible="disabled"
      showArrow={false}
      header={(
        <BrowseRow
          label={label}
          isActive={isActive}
          stopPropagation
          onSelect={onSelect}
        />
      )}
    />
  );
}

function BrowseTree({
  browseGroups,
  activeBucket,
  expandedProjectKeys,
  onExpandChange,
  onSelectBucket,
  showNoneCostcodeOption,
  showGlobalCostcodes,
  projectSearchText = '',
  t,
}) {
  const normalizedSearch = String(projectSearchText ?? '').trim().toLowerCase();
  const showNone = showNoneCostcodeOption && (
    !normalizedSearch || 'none'.includes(normalizedSearch)
  );
  const showGlobal = showGlobalCostcodes && (
    !normalizedSearch || 'global'.includes(normalizedSearch)
  );
  const hasResults = showNone || showGlobal || browseGroups.length > 0;

  if (!hasResults) {
    return (
      <div className="report-costcode-filter-empty report-costcode-filter-browse-empty">
        {`No ${t('Project', { count: 2 }).toLowerCase()} found.`}
      </div>
    );
  }

  return (
    <>
      {showNone && (
        <BrowseRow
          label="None"
          isNone
          isActive={activeBucket?.key === BUCKET_NONE.key}
          onSelect={() => onSelectBucket(BUCKET_NONE)}
        />
      )}
      <Collapse
        bordered={false}
        activeKey={expandedProjectKeys}
        onChange={onExpandChange}
        className="report-costcode-filter-browse"
      >
        {showGlobal && (
          <Panel
            header={<span className="report-costcode-filter-browse-header">Global</span>}
            key="global"
          >
            <BrowseRow
              label="Global"
              indented
              isActive={activeBucket?.key === BUCKET_GLOBAL.key}
              onSelect={() => onSelectBucket(BUCKET_GLOBAL)}
            />
          </Panel>
        )}
        {browseGroups.map((project) => (
          <Panel
            header={<span className="report-costcode-filter-browse-header">{project.label}</span>}
            key={project.id}
          >
            <Collapse bordered={false}>
              <BucketPanel
                label="Unphased"
                isActive={activeBucket?.key === `unphased:${project.id}`}
                onSelect={() => onSelectBucket(
                  { type: 'unphased', key: `unphased:${project.id}`, projectId: project.id },
                )}
              />
              {project.phases.map((phase) => (
                <BucketPanel
                  key={`phase:${phase.id}`}
                  label={phase.name}
                  isActive={activeBucket?.key === `phase:${phase.id}`}
                  onSelect={() => onSelectBucket(
                    { type: 'phase', key: `phase:${phase.id}`, phaseId: phase.id },
                    phase.name,
                  )}
                />
              ))}
            </Collapse>
          </Panel>
        ))}
      </Collapse>
    </>
  );
}

function ShowingColumn({
  showList,
  isSearching,
  listOptions,
  listLoading,
  listHeight,
  activeGroupLabel,
  optionMap,
  checked,
  onToggleValue,
  browseHint,
}) {
  const contextLabel = isSearching ? 'Search results' : activeGroupLabel;
  const options = dedupeOptionsByLabel(listOptions, optionMap);

  return (
    <>
      {contextLabel && (
        <div className="report-costcode-filter-context-label">{contextLabel}</div>
      )}
      {showList ? (
        <OptionList
          options={options}
          loading={listLoading}
          listHeight={listHeight}
          checked={checked}
          onToggleValue={onToggleValue}
        />
      ) : (
        <div className="report-costcode-filter-hint report-costcode-filter-hint--italic">
          {browseHint}
        </div>
      )}
    </>
  );
}

function FilterMenu({
  searchText,
  onSearchChange,
  projectSearchText,
  onProjectSearchChange,
  onClearAll,
  browseGroups,
  activeBucket,
  expandedProjectKeys,
  onExpandChange,
  onSelectBucket,
  isSearching,
  listOptions,
  listLoading,
  activeGroupLabel,
  selectedOptions,
  optionMap,
  checked,
  onToggleValue,
  showNoneCostcodeOption,
  showGlobalCostcodes,
  browseHint,
  t,
}) {
  const selectedCount = selectedOptions.length;
  const selectedTitle = selectedCount ? `Selected (${selectedCount})` : 'Selected';
  const contextRows = (isSearching ? 'Search results' : activeGroupLabel) ? 18 : 0;
  const listHeight = MENU_LIST_HEIGHT - contextRows;
  const showList = isSearching || !!activeBucket;

  return (
    <div className="report-costcode-filter-menu">
      <Row align="middle" className="report-costcode-filter-menu-toolbar" gutter={8}>
        <Col span={17}>
          <Input.Search
            className="searchbar"
            placeholder="Search by code or name"
            value={searchText}
            onChange={(e) => onSearchChange(e?.target?.value ?? '')}
            allowClear
          />
        </Col>
        <Col span={7} style={{ textAlign: 'right' }}>
          <OnTraccrButton
            title="Clear All"
            onClick={onClearAll}
            className="report-costcode-filter-clear-btn"
          />
        </Col>
      </Row>

      <div className="report-costcode-filter-menu-body">
        <MenuColumn title="Browse" browse>
          <div className="report-costcode-filter-browse-column">
            <Input
              className="report-costcode-filter-browse-search"
              placeholder={`Filter ${t('Project', { count: 2 }).toLowerCase()}`}
              value={projectSearchText}
              onChange={(e) => onProjectSearchChange(e?.target?.value ?? '')}
              allowClear
            />
            <div className="report-costcode-filter-browse-tree">
              <BrowseTree
                browseGroups={browseGroups}
                activeBucket={activeBucket}
                expandedProjectKeys={expandedProjectKeys}
                onExpandChange={onExpandChange}
                onSelectBucket={onSelectBucket}
                showNoneCostcodeOption={showNoneCostcodeOption}
                showGlobalCostcodes={showGlobalCostcodes}
                projectSearchText={projectSearchText}
                t={t}
              />
            </div>
          </div>
        </MenuColumn>
        <MenuColumn title="Showing">
          <ShowingColumn
            showList={showList}
            isSearching={isSearching}
            listOptions={listOptions}
            listLoading={listLoading}
            listHeight={listHeight}
            activeGroupLabel={activeGroupLabel}
            optionMap={optionMap}
            checked={checked}
            onToggleValue={onToggleValue}
            browseHint={browseHint}
          />
        </MenuColumn>
        <MenuColumn title={selectedTitle} isLast>
          {selectedOptions.length ? (
            <OptionList
              options={selectedOptions}
              loading={false}
              checked={checked}
              onToggleValue={onToggleValue}
            />
          ) : (
            <div className="report-costcode-filter-hint report-costcode-filter-hint--italic">
              All cost codes are included.
            </div>
          )}
        </MenuColumn>
      </div>
    </div>
  );
}

export default function ReportCostcodeFilterCard({
  selected,
  initialOptions = [],
  fetchOptions,
  selectedProjects,
  projects = [],
  phases = [],
  onFilterToggle,
  onCheckChanged,
  checked: columnChecked = true,
  hoverHelp = null,
  title,
  showNoneCostcodeOption = true,
  showGlobalCostcodes = true,
}) {
  const { t } = useTranslation();
  const company = useSelector((state) => state.settings.company);
  const settings = company?.settings ?? {};
  const searchDebouncer = useRef(new Debouncer());

  const [checked, setChecked] = useState(() => new Set(selected ?? []));
  const [optionMap, setOptionMap] = useState(() => buildInitialOptionMap(initialOptions));
  const [searchText, setSearchText] = useState('');
  const [projectSearchText, setProjectSearchText] = useState('');
  const [activeBucket, setActiveBucket] = useState(null);
  const [bucketOptions, setBucketOptions] = useState([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [expandedProjectKeys, setExpandedProjectKeys] = useState([]);

  useEffect(() => () => searchDebouncer.current.clear(), []);

  useEffect(() => {
    setOptionMap((prev) => {
      const next = { ...prev };
      let changed = false;
      initialOptions.forEach((opt) => {
        if (next[opt.value] !== opt.label) {
          next[opt.value] = opt.label;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [initialOptions]);

  useEffect(() => {
    setChecked(new Set(selected ?? []));
  }, [selected]);

  const browseGroups = useMemo(
    () => buildCostcodeFilterCategories({ projects, phases, settings }),
    [projects, phases, settings],
  );

  const filteredBrowseGroups = useMemo(
    () => filterBrowseGroups(browseGroups, projectSearchText),
    [browseGroups, projectSearchText],
  );

  const loadBucket = useCallback(async (bucket) => {
    const params = getCostcodeBucketFetchParams(bucket);
    if (!params || typeof fetchOptions !== 'function') {
      setBucketLoading(false);
      setBucketOptions([]);
      return;
    }
    try {
      const options = (await fetchOptions(params)) || [];
      setOptionMap((prev) => {
        const next = { ...prev };
        options.forEach((opt) => { next[opt.value] = opt.label; });
        return next;
      });
      setBucketOptions(options);
      setBucketLoading(false);
    } catch {
      setBucketLoading(false);
      setBucketOptions([]);
    }
  }, [fetchOptions]);

  const selectBucket = useCallback((bucket, phaseName) => {
    const bucketWithMeta = phaseName ? { ...bucket, phaseName } : bucket;
    if (bucketWithMeta.type === 'none') {
      setActiveBucket(bucketWithMeta);
      setBucketOptions([NONE_COSTCODE_OPTION]);
      setBucketLoading(false);
      setSearchText('');
      setSearchOptions([]);
      return;
    }
    setActiveBucket(bucketWithMeta);
    setBucketOptions([]);
    setBucketLoading(true);
    setSearchText('');
    setSearchOptions([]);
    loadBucket(bucketWithMeta);
  }, [loadBucket]);

  const runSearch = useCallback(async (query) => {
    if (typeof fetchOptions !== 'function') return;
    setSearchLoading(true);
    try {
      const params = makeCostcodeSearchParams({
        selectedProjects,
        allProjects: projects,
        settings,
        search: query,
      });
      const options = (await fetchOptions(params)) || [];
      const includeNone = showNoneCostcodeOption
        && query
        && NONE_COSTCODE_OPTION.label.toLowerCase().includes(query.toLowerCase());
      const searchResults = includeNone
        ? [NONE_COSTCODE_OPTION].concat(options)
        : options;
      setOptionMap((prev) => {
        const next = { ...prev };
        searchResults.forEach((opt) => { next[opt.value] = opt.label; });
        return next;
      });
      setSearchOptions(searchResults);
      setSearchLoading(false);
    } catch {
      setSearchLoading(false);
    }
  }, [fetchOptions, selectedProjects, projects, settings, showNoneCostcodeOption]);

  const onSearchChange = useCallback((value) => {
    setSearchText(value);
    setActiveBucket(null);
    setBucketOptions([]);
    if (!value?.trim()) {
      searchDebouncer.current.clear();
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }
    searchDebouncer.current.debounce(
      () => runSearch(value),
      SEARCH_DEBOUNCE_MS,
    );
  }, [runSearch]);

  const toggleValue = useCallback((option, isChecked) => {
    const values = option?.values || [option?.value ?? option];
    setChecked((prev) => {
      const next = new Set(prev);
      values.forEach((value) => {
        if (isChecked) {
          next.add(value);
        } else {
          next.delete(value);
        }
      });
      onFilterToggle(next);
      return next;
    });
  }, [onFilterToggle]);

  const onClearAll = useCallback(() => {
    const cleared = new Set();
    onFilterToggle(cleared);
    setChecked(cleared);
    setActiveBucket(null);
    setBucketOptions([]);
    setSearchText('');
    setSearchOptions([]);
    setProjectSearchText('');
  }, [onFilterToggle]);

  const onPopoverVisibleChange = useCallback((visible) => {
    if (!visible) {
      setSearchText('');
      setSearchOptions([]);
      setProjectSearchText('');
    }
  }, []);

  const isSearching = !!searchText?.trim();
  const listOptions = isSearching ? searchOptions : bucketOptions;
  const listLoading = isSearching ? searchLoading : bucketLoading;
  const activeGroupLabel = getActiveGroupLabel(activeBucket, browseGroups);
  const selectedOptions = useMemo(
    () => getSelectedOptions(checked, optionMap),
    [checked, optionMap],
  );
  const browseHint = useMemo(
    () => getBrowseHint(showNoneCostcodeOption, showGlobalCostcodes, t),
    [showNoneCostcodeOption, showGlobalCostcodes, t],
  );

  const triggerColor = settings?.uxRedesignPt1
    ? Colors.REDESIGN_PRIMARY_TEXT
    : 'black';

  const menu = (
    <FilterMenu
      searchText={searchText}
      onSearchChange={onSearchChange}
      projectSearchText={projectSearchText}
      onProjectSearchChange={setProjectSearchText}
      onClearAll={onClearAll}
      browseGroups={filteredBrowseGroups}
      activeBucket={activeBucket}
      expandedProjectKeys={expandedProjectKeys}
      onExpandChange={setExpandedProjectKeys}
      onSelectBucket={selectBucket}
      isSearching={isSearching}
      listOptions={listOptions}
      listLoading={listLoading}
      activeGroupLabel={activeGroupLabel}
      selectedOptions={selectedOptions}
      optionMap={optionMap}
      checked={checked}
      onToggleValue={toggleValue}
      showNoneCostcodeOption={showNoneCostcodeOption}
      showGlobalCostcodes={showGlobalCostcodes}
      browseHint={browseHint}
      t={t}
    />
  );

  return (
    <Row className="report-costcode-filter-card" align="middle" gutter={4}>
      <Col>
        <OnTraccrCheckbox
          onChange={onCheckChanged}
          value={columnChecked}
          checkboxStyle={{ marginRight: 0 }}
        />
      </Col>
      <Col>
        <Popover
          content={menu}
          trigger="click"
          placement="bottomRight"
          getPopupContainer={(node) => node.parentNode}
          onVisibleChange={onPopoverVisibleChange}
        >
          <BorderlessButton
            title={title}
            className="report-costcode-filter-trigger"
            style={{ color: triggerColor }}
            iconNode={<DownOutlined />}
          />
        </Popover>
      </Col>
      {hoverHelp && <Col>{hoverHelp}</Col>}
    </Row>
  );
}

/* eslint-enable react/prop-types */
ReportCostcodeFilterCard.propTypes = {
  selected: PropTypes.instanceOf(Set),
  initialOptions: PropTypes.arrayOf(costcodeOptionShape),
  fetchOptions: PropTypes.func,
  selectedProjects: PropTypes.instanceOf(Set),
  projects: PropTypes.arrayOf(PropTypes.shape({})),
  phases: PropTypes.arrayOf(PropTypes.shape({})),
  onFilterToggle: PropTypes.func.isRequired,
  onCheckChanged: PropTypes.func,
  checked: PropTypes.bool,
  hoverHelp: PropTypes.node,
  title: PropTypes.string,
  showNoneCostcodeOption: PropTypes.bool,
  showGlobalCostcodes: PropTypes.bool,
};

ReportCostcodeFilterCard.defaultProps = {
  selected: new Set(),
  initialOptions: [],
  fetchOptions: null,
  selectedProjects: new Set(),
  projects: [],
  phases: [],
  onCheckChanged: () => {},
  checked: true,
  hoverHelp: null,
  title: 'Cost Code',
  showNoneCostcodeOption: true,
  showGlobalCostcodes: true,
};
